import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { lineTemplatesApi } from "@/api";
import type { LineTemplate, LineMessageType } from "@/types";
import { format } from "date-fns";
import { Plus, Save, Trash2, X, MessagesSquare, Code, Eye } from "lucide-react";

export default function LineTemplates() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<LineTemplate | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [tab, setTab] = useState<"edit" | "preview">("edit");

  const { data, isLoading } = useQuery({
    queryKey: ["line-templates"],
    queryFn: () => lineTemplatesApi.list(),
  });

  const saveMutation = useMutation({
    mutationFn: (tpl: { id?: string; name: string; type: string; body?: string | null; altText?: string | null; flexJson?: Record<string, unknown> | null }) => {
      const clean = { ...tpl, body: tpl.body ?? undefined, altText: tpl.altText ?? undefined, flexJson: tpl.flexJson ?? undefined };
      if (tpl.id) return lineTemplatesApi.update(tpl.id, clean);
      return lineTemplatesApi.create(clean);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["line-templates"] });
      setEditing(null);
      setIsNew(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => lineTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["line-templates"] });
      setEditing(null);
    },
  });

  const handleNew = () => {
    setEditing({ id: "", name: "", type: "TEXT", body: "", altText: null, flexJson: null, createdAt: "", updatedAt: "" });
    setIsNew(true);
    setTab("edit");
  };

  const handleEdit = async (id: string) => {
    const tpl = await lineTemplatesApi.get(id);
    setEditing(tpl);
    setIsNew(false);
    setTab("edit");
  };

  const handleSave = () => {
    if (!editing || !editing.name.trim()) return;
    if (editing.type === "TEXT" && !editing.body?.trim()) return;
    if (editing.type === "FLEX" && !editing.flexJson) return;

    saveMutation.mutate({
      ...(isNew ? {} : { id: editing.id }),
      name: editing.name,
      type: editing.type,
      body: editing.body,
      altText: editing.altText,
      flexJson: editing.flexJson,
    });
  };

  const setFlexJson = (jsonStr: string) => {
    try {
      const parsed = JSON.parse(jsonStr);
      setEditing(editing ? { ...editing, flexJson: parsed } : null);
    } catch {
      // invalid JSON, keep raw string in body for editing
    }
  };

  const canSave = editing && editing.name.trim() && (
    (editing.type === "TEXT" && editing.body?.trim()) ||
    (editing.type === "FLEX" && editing.flexJson)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">LINEテンプレート</h2>
        {!editing && (
          <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#06C755] text-white rounded-md text-sm hover:opacity-90"
          >
            <Plus size={16} />
            新規テンプレート
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4 max-w-2xl">
          <div className="bg-white p-4 rounded-lg shadow-sm border grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">テンプレート名</label>
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="キャンペーン通知"
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">メッセージタイプ</label>
              <select
                value={editing.type}
                onChange={(e) => setEditing({ ...editing, type: e.target.value as LineMessageType })}
                className="w-full px-3 py-2 border rounded-md text-sm"
              >
                <option value="TEXT">テキスト</option>
                <option value="FLEX">Flex Message</option>
              </select>
            </div>
          </div>

          {editing.type === "TEXT" ? (
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <label className="block text-xs font-medium text-slate-600 mb-1">本文</label>
              <textarea
                value={editing.body || ""}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                rows={6}
                placeholder={"{{lastName}}様\n\n新商品のご案内です。\n詳細はこちら: https://..."}
                className="w-full px-3 py-2 border rounded-md text-sm resize-y"
              />
              <p className="text-xs text-slate-400 mt-1">
                変数: {"{{lastName}}"}, {"{{firstName}}"}, {"{{company}}"} が使用可能
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="flex items-center border-b bg-slate-50 px-4 py-2 gap-1">
                <button
                  onClick={() => setTab("edit")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium ${
                    tab === "edit" ? "bg-white shadow-sm text-slate-800" : "text-slate-500"
                  }`}
                >
                  <Code size={12} /> JSON
                </button>
                <button
                  onClick={() => setTab("preview")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium ${
                    tab === "preview" ? "bg-white shadow-sm text-slate-800" : "text-slate-500"
                  }`}
                >
                  <Eye size={12} /> プレビュー
                </button>
              </div>
              {tab === "edit" ? (
                <div className="p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">通知テキスト (altText)</label>
                    <input
                      value={editing.altText || ""}
                      onChange={(e) => setEditing({ ...editing, altText: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md text-sm"
                      placeholder="キャンペーンのお知らせ"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Flex Message JSON</label>
                    <textarea
                      value={editing.flexJson ? JSON.stringify(editing.flexJson, null, 2) : ""}
                      onChange={(e) => setFlexJson(e.target.value)}
                      rows={16}
                      className="w-full px-3 py-2 border rounded-md text-sm font-mono resize-y"
                      placeholder='{"type": "bubble", "body": { ... }}'
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-slate-100 p-6 flex justify-center" style={{ minHeight: 300 }}>
                  <div className="bg-white rounded-2xl shadow-lg p-4 max-w-xs w-full">
                    <pre className="text-xs whitespace-pre-wrap break-words text-slate-600">
                      {editing.flexJson ? JSON.stringify(editing.flexJson, null, 2) : "(JSONを入力してください)"}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview for TEXT */}
          {editing.type === "TEXT" && (
            <div className="bg-slate-100 p-4 rounded-lg">
              <h4 className="text-xs font-medium text-slate-500 mb-2">プレビュー</h4>
              <div className="bg-white rounded-2xl p-4 max-w-xs mx-auto shadow-sm border">
                <div className="bg-[#E8F5E9] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words">
                  {(editing.body || "")
                    .replace(/\{\{lastName\}\}/g, "テスト")
                    .replace(/\{\{firstName\}\}/g, "太郎")
                    .replace(/\{\{company\}\}/g, "テスト株式会社")
                    || "(本文を入力してください)"}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending || !canSave}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              <Save size={16} />
              {saveMutation.isPending ? "保存中..." : "保存"}
            </button>
            {!isNew && (
              <button
                onClick={() => { if (confirm("このテンプレートを削除しますか？")) deleteMutation.mutate(editing.id); }}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
              >
                <Trash2 size={16} />
                削除
              </button>
            )}
            <button
              onClick={() => { setEditing(null); setIsNew(false); }}
              className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-slate-50"
            >
              <X size={16} />
              キャンセル
            </button>
          </div>
        </div>
      ) : isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : !data?.data.length ? (
        <div className="text-center py-12 text-slate-400">
          <MessagesSquare size={40} className="mx-auto mb-2 opacity-40" />
          <p>LINEテンプレートがありません</p>
          <p className="text-sm mt-1">「新規テンプレート」ボタンから作成してください</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.data.map((tpl) => (
            <div
              key={tpl.id}
              onClick={() => handleEdit(tpl.id)}
              className="bg-white rounded-lg shadow-sm border hover:border-[#06C755] cursor-pointer transition-colors p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <MessagesSquare size={16} className="text-[#06C755]" />
                <h3 className="font-medium text-sm truncate">{tpl.name}</h3>
                <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{tpl.type}</span>
              </div>
              <p className="text-xs text-slate-500 line-clamp-3">
                {tpl.type === "TEXT" ? tpl.body : tpl.altText || "Flex Message"}
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {format(new Date(tpl.updatedAt), "yyyy/MM/dd")}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
